import React from 'react';
import { RadioGroup } from '@headlessui/react';
import classnames from 'classnames';

export interface Option<T> {
  label: string | React.ReactNode;
  value: T;
  disabled?: boolean;
}

export type Props<T> = Omit<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
  'onChange' | 'value'
> & {
  options: Option<T>[];
  value: T;
  onChange: (value: T) => void;
  error?: string;
  disabled?: boolean;
  className?: string;
};

const ForwardRefRadioInput = <T extends unknown>(
  { options, error, className, ...props }: Props<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) => (
  <>
    <RadioGroup
      as="div"
      {...props}
      className={classnames('flex flex-wrap gap-x-3.5 gap-y-0.5', className)}
      ref={ref}
    >
      {options.map(({ label, value, ...optionAttrs }) => (
        <RadioGroup.Option
          {...optionAttrs}
          value={value}
          key={JSON.stringify(value)}
        >
          {({ checked, active, disabled }) => (
            <div
              className={classnames('flex items-center w-full cursor-pointer', {
                group: !disabled,
              })}
            >
              <div
                className={classnames(
                  'flex items-center justify-center rounded-full w-6 h-6 mr-1.5',
                  {
                    'ring-2 ring-inset ring-primary': active,
                  }
                )}
              >
                <div
                  className={classnames(
                    'w-4 h-4 bg-gray-dark600 ring-1 ring-gray-dark500 group-hover:ring-gray-dark400 ring-inset rounded-full',
                    {
                      'ring-5': checked,
                      '!ring-primary': checked,
                      '!bg-gray-dark700 !ring-gray-dark600': disabled,
                    }
                  )}
                ></div>
              </div>
              {label && (
                <span
                  className={classnames('text-gray-dark500', {
                    '!text-gray-dark400 opacity-30': disabled,
                    '!text-gray-dark400': !disabled && checked,
                  })}
                >
                  {label}
                </span>
              )}
            </div>
          )}
        </RadioGroup.Option>
      ))}
    </RadioGroup>
    {error && <p className="text-red-500 text-xs mt-3">{error}</p>}
  </>
);

export const CheckboxInput = React.forwardRef(ForwardRefRadioInput) as <T>(
  props: Props<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => ReturnType<typeof ForwardRefRadioInput>;

export default CheckboxInput;
